import { useEffect, useState, useCallback } from 'react'
import { ShieldCheck, Plus, Pencil, Trash2, Plug, ScrollText, Save, Zap } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal, confirmModal } from '../components/ui'

// ADMIN — Proteção Veicular: catálogo de planos (manual-first), config da integração
// LabelView/CSMVNO (token write-only) e auditoria das chamadas (PRD §17).
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'
const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const fmt = (v) => (v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-')
const PLANO_VAZIO = { nome: '', descricao: '', coberturas: '', beneficios: '', franquia: '', valor_mensal: '', adesao: '', ordem: 0, ativo: true }

export default function PvAdmin() {
  const [aba, setAba] = useState('planos')
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2">
          <ShieldCheck size={24} className="text-accent-500" /> Proteção Veicular — Gestão
        </h1>
        <p className="text-sm text-neutral-500 mt-1">Planos, integração com o parceiro e auditoria das chamadas</p>
      </div>
      <div className="flex gap-1 bg-neutral-100 rounded-xl p-1 w-fit">
        {[['planos', 'Planos'], ['integracao', 'Integração'], ['logs', 'Auditoria']].map(([v, l]) => (
          <button key={v} onClick={() => setAba(v)} className={`px-4 py-2 rounded-lg text-sm font-semibold ${aba === v ? 'bg-white shadow text-accent-700' : 'text-neutral-500'}`}>{l}</button>
        ))}
      </div>
      {aba === 'planos' && <Planos />}
      {aba === 'integracao' && <Integracao />}
      {aba === 'logs' && <Logs />}
    </div>
  )
}

/* ═════════ Planos (CRUD) ═════════ */
function Planos() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)  // null | {id?, ...}
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/protecao-veicular/planos-admin').then((r) => setRows(r.data.data || [])).catch(() => {}).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  async function salvar() {
    if (!form.nome.trim()) return errorModal('Informe o nome do plano.')
    setBusy(true)
    try {
      const body = { ...form, valor_mensal: Number(form.valor_mensal || 0), adesao: form.adesao === '' ? null : Number(form.adesao), ordem: Number(form.ordem || 0) }
      // coberturas/beneficios podem vir como array do backend — normaliza p/ string
      if (Array.isArray(body.coberturas)) body.coberturas = body.coberturas.join('\n')
      if (Array.isArray(body.beneficios)) body.beneficios = body.beneficios.join('\n')
      if (form.id) await api.put(`/protecao-veicular/planos-admin/${form.id}`, body)
      else await api.post('/protecao-veicular/planos-admin', body)
      await successModal('Plano salvo.'); setForm(null); load()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao salvar') } finally { setBusy(false) }
  }
  async function excluir(id) {
    if (!(await confirmModal('Excluir este plano?', { title: 'Excluir plano', okLabel: 'Excluir' }))) return
    api.delete(`/protecao-veicular/planos-admin/${id}`).then(load).catch(() => {})
  }

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-bold text-graphite">Planos cadastrados</p>
          <button onClick={() => setForm({ ...PLANO_VAZIO })} className="inline-flex items-center gap-1 rounded-lg bg-accent-500 text-white text-xs font-bold px-3 py-1.5"><Plus size={14} /> Novo plano</button>
        </div>
        {loading ? <Loader text="Carregando..." /> : rows.length === 0 ? (
          <p className="text-sm text-neutral-400">Nenhum plano — cadastre os planos que aparecem na cotação (enquanto a integração LabelView não estiver ativa).</p>
        ) : (
          <div className="space-y-2">
            {rows.map((p) => (
              <div key={p.id} className={`border rounded-xl p-3 flex items-start gap-2 ${p.ativo ? 'border-neutral-200' : 'border-neutral-100 opacity-60'}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-graphite">{p.nome} {!p.ativo && <span className="text-[10px] bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded">INATIVO</span>}</p>
                  <p className="text-accent-600 font-heading font-extrabold">{money(p.valor_mensal)}<span className="text-[11px] text-neutral-400 font-normal">/mês</span>{p.franquia ? <span className="text-[11px] text-neutral-500 font-normal"> · franquia {p.franquia}</span> : null}</p>
                  <p className="text-[11px] text-neutral-500 truncate">{(p.coberturas || []).join(' · ')}</p>
                </div>
                <button onClick={() => setForm({ ...p, coberturas: (p.coberturas || []).join('\n'), beneficios: (p.beneficios || []).join('\n'), valor_mensal: p.valor_mensal, adesao: p.adesao ?? '' })} className="p-1.5 rounded-lg hover:bg-accent-50 text-accent-600"><Pencil size={14} /></button>
                <button onClick={() => excluir(p.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {form && (
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5 space-y-3 h-fit">
          <p className="font-heading font-bold text-graphite">{form.id ? `Editar: ${form.nome}` : 'Novo plano'}</p>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label><input className={inp} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Valor mensal (R$) *</label><input type="number" className={inp} value={form.valor_mensal} onChange={(e) => setForm({ ...form, valor_mensal: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Adesão (R$)</label><input type="number" className={inp} value={form.adesao} onChange={(e) => setForm({ ...form, adesao: e.target.value })} /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Ordem</label><input type="number" className={inp} value={form.ordem} onChange={(e) => setForm({ ...form, ordem: e.target.value })} /></div>
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Franquia</label><input className={inp} value={form.franquia || ''} onChange={(e) => setForm({ ...form, franquia: e.target.value })} placeholder="ex.: 5% da FIPE (mín. R$ 1.500)" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Coberturas (1 por linha)</label><textarea rows={4} className={inp} value={form.coberturas || ''} onChange={(e) => setForm({ ...form, coberturas: e.target.value })} placeholder={'Roubo e furto\nColisão\nIncêndio\nAssistência 24h'} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Benefícios (1 por linha)</label><textarea rows={3} className={inp} value={form.beneficios || ''} onChange={(e) => setForm({ ...form, beneficios: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={!!form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} className="w-4 h-4 accent-accent-500" /> Ativo</label>
          <div className="flex gap-2">
            <button onClick={salvar} disabled={busy} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-bold shadow-md flex items-center gap-1.5"><Save size={14} /> Salvar</button>
            <button onClick={() => setForm(null)} className="rounded-xl border border-neutral-200 text-neutral-500 px-4 py-2 text-sm">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ═════════ Integração (config parceiros) ═════════ */
function Integracao() {
  const [parceiro, setParceiro] = useState('labelview')
  const [cfg, setCfg] = useState(null)
  const [form, setForm] = useState({ base_url: '', token: '', ambiente: 'producao', ativo: false })
  const [busy, setBusy] = useState(false)
  const [teste, setTeste] = useState(null)

  const load = useCallback(() => {
    api.get(`/integracoes-parceiros/${parceiro}`).then((r) => {
      setCfg(r.data)
      setForm({ base_url: r.data.base_url || '', token: '', ambiente: r.data.ambiente || 'producao', ativo: !!r.data.ativo })
      setTeste(null)
    }).catch(() => {})
  }, [parceiro])
  useEffect(() => { load() }, [load])

  async function salvar() {
    setBusy(true)
    try {
      const body = { base_url: form.base_url, ambiente: form.ambiente, ativo: form.ativo }
      if (form.token.trim()) body.token = form.token.trim()
      await api.put(`/integracoes-parceiros/${parceiro}`, body)
      await successModal('Configuração salva.'); load()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao salvar') } finally { setBusy(false) }
  }
  async function testar() {
    setBusy(true); setTeste(null)
    try { const r = await api.post(`/integracoes-parceiros/${parceiro}/testar`); setTeste(r.data) }
    catch (e) { setTeste({ ok: false, erro: e?.response?.data?.detail || 'Falha no teste' }) } finally { setBusy(false) }
  }

  return (
    <div className="max-w-2xl bg-white rounded-2xl border border-neutral-200 shadow-sm p-5 space-y-3">
      <div className="flex gap-1 bg-neutral-100 rounded-xl p-1 w-fit">
        {[['labelview', 'LabelView — Proteção Veicular'], ['csmvno', 'CSMVNO — Telecom']].map(([v, l]) => (
          <button key={v} onClick={() => setParceiro(v)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${parceiro === v ? 'bg-white shadow text-accent-700' : 'text-neutral-500'}`}>{l}</button>
        ))}
      </div>
      {parceiro === 'labelview' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          A documentação oficial da API LabelView de Proteção Veicular ainda não foi fornecida — o módulo opera em <b>modo manual</b> (planos do catálogo + avanço de status pelo admin). Configure aqui quando receber URL/token; o conector já está preparado.
        </p>
      )}
      <div className="flex items-center gap-2 text-sm text-neutral-600"><Plug size={15} className="text-accent-600" />
        Status: <b className={cfg?.ativo ? 'text-emerald-600' : 'text-neutral-500'}>{cfg?.ativo ? 'ATIVA' : (cfg?.configurado ? 'Configurada (inativa)' : 'Não configurada — modo manual')}</b>
        {cfg?.token_mascarado && <span className="text-[11px] text-neutral-400">token {cfg.token_mascarado}</span>}
      </div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Base URL</label>
        <input className={inp} value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
          placeholder={parceiro === 'csmvno' ? 'https://api.csmvno.com.br/api_producao/index.php' : 'https://... (aguardando doc LabelView)'} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Token (write-only)</label>
          <input type="password" className={inp} value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder="Deixe em branco p/ manter" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Ambiente</label>
          <select className={inp} value={form.ambiente} onChange={(e) => setForm({ ...form, ambiente: e.target.value })}>
            <option value="producao">Produção</option><option value="sandbox">Sandbox</option>
          </select></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} className="w-4 h-4 accent-accent-500" /> Integração ativa</label>
      <div className="flex gap-2">
        <button onClick={salvar} disabled={busy} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-bold shadow-md flex items-center gap-1.5"><Save size={14} /> Salvar</button>
        <button onClick={testar} disabled={busy} className="rounded-xl border border-accent-200 text-accent-700 px-4 py-2 text-sm font-semibold flex items-center gap-1.5"><Zap size={14} /> Testar conexão</button>
      </div>
      {teste && (
        <p className={`text-xs rounded-lg px-3 py-2 ${teste.ok ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'}`}>
          {teste.ok ? `Conexão OK — ${teste.amostra || ''}` : `Falha: ${teste.erro}`}
        </p>
      )}
    </div>
  )
}

/* ═════════ Auditoria (logs) ═════════ */
function Logs() {
  const [parceiro, setParceiro] = useState('labelview')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    setLoading(true)
    api.get(`/integracoes-parceiros/${parceiro}/logs`).then((r) => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false))
  }, [parceiro])
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="font-heading font-bold text-graphite flex items-center gap-2"><ScrollText size={17} className="text-accent-600" /> Auditoria de chamadas</p>
        <div className="flex gap-1 bg-neutral-100 rounded-xl p-1">
          {[['labelview', 'LabelView'], ['csmvno', 'CSMVNO']].map(([v, l]) => (
            <button key={v} onClick={() => setParceiro(v)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${parceiro === v ? 'bg-white shadow text-accent-700' : 'text-neutral-500'}`}>{l}</button>
          ))}
        </div>
      </div>
      {loading ? <Loader text="Carregando..." /> : rows.length === 0 ? <p className="text-sm text-neutral-400">Nenhuma chamada registrada.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-neutral-100 text-left text-[11px] uppercase tracking-wider text-neutral-400">
              <th className="py-2 pr-3">Data</th><th className="py-2 pr-3">Endpoint</th><th className="py-2 pr-3">HTTP</th>
              <th className="py-2 pr-3">Tempo</th><th className="py-2 pr-3">Tent.</th><th className="py-2">Ref / Erro</th></tr></thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id} className="border-b border-neutral-50">
                  <td className="py-2 pr-3 text-xs text-neutral-500 whitespace-nowrap">{fmt(x.created_at)}</td>
                  <td className="py-2 pr-3 text-xs">{x.endpoint}</td>
                  <td className={`py-2 pr-3 text-xs font-bold ${x.http_status === 200 ? 'text-emerald-600' : 'text-red-600'}`}>{x.http_status || '-'}</td>
                  <td className="py-2 pr-3 text-xs">{x.tempo_ms != null ? `${x.tempo_ms}ms` : '-'}</td>
                  <td className="py-2 pr-3 text-xs">{x.tentativas}</td>
                  <td className="py-2 text-xs text-neutral-500 max-w-[280px] truncate">{x.referencia || ''} {x.erro || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
